import {ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot} from '@angular/router';
import {inject, Injectable} from "@angular/core";
import {CustomerService} from "../services/customer.service";
import {RouterService} from "../services/router.service";
import {ApiService} from '../services/api.service';
import {catchError, map, Observable, of, switchMap, take, tap} from 'rxjs';
import {TokenService} from "../services/token.service";
import {FirebaseService} from "../services/firebase.service";
import {CustomerGdprResource} from '../types/settings-types/customerGdprResource';
import {HttpResponse} from '@angular/common/http';
import {ProfileService} from '../services/profile.service';

@Injectable({providedIn: 'root'})
export class customerUserGuard implements CanActivate {

  private customerService: CustomerService = inject(CustomerService);
  private apiService: ApiService = inject(ApiService);
  private routerService: RouterService = inject(RouterService);
  private tokenService: TokenService = inject(TokenService);
  private firebaseService: FirebaseService = inject(FirebaseService);
  private profileService: ProfileService = inject(ProfileService);

  private gdprFine: boolean = false;

  public canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const requestedUrl = state.url;

    return this.loadCustomerIfNeeded(requestedUrl).pipe(
      switchMap(ok => ok ? this.loadUserIfNeeded(requestedUrl) : of(false)),
      switchMap(ok => ok ? this.checkNotes(requestedUrl) : of(false)),
      catchError(() => this.redirect('/auth/customer', requestedUrl))
    );
  }

  private loadCustomerIfNeeded(requestedUrl: string): Observable<boolean> {
    const customerKey = this.customerService.getStoredCustomerKey();

    if (!customerKey) {
      return this.redirect('/auth/customer', requestedUrl);
    }

    this.customerService.setCustomerKey(customerKey);

    if (this.customerService.publicCustomerResource()) {
      return of(true);
    }

    return this.customerService.getPublicCustomerResourceCached(customerKey).pipe(
      tap(customerPublicResource => {
        this.customerService.setPublicCustomerResource(customerPublicResource);
      }),
      map(() => true),
      catchError(() => this.redirect('/auth/customer', requestedUrl))
    );
  }

  private loadUserIfNeeded(requestedUrl?: string): Observable<boolean> {
    // Check if user has a token first
    if (!this.tokenService.hasToken()) {
      return this.redirect('/auth/login', requestedUrl);
    }

    if(this.profileService.profile() === null) {
      this.profileService.loadUserProfile();
      this.firebaseService.init();
    }
    return of(true);
  }

  private checkNotes(requestedUrl: string): Observable<boolean> {
    if (!this.customerService.publicCustomerResource()) {
      return of(false);
    }

    if(this.gdprFine) {
      return of(true);
    } else {
      return this.apiService.getUserGdpr()
      .pipe(
        take(1),
        switchMap((gdprResponse: HttpResponse<CustomerGdprResource>) => {
          if(gdprResponse.status === 304 || gdprResponse.status === 204) {
            this.gdprFine = true;
            return of(true);
          } else if(gdprResponse.status === 200) {
            // User needs to accept GDPR
            // If already on the notes page, allow access to prevent loop
            if(requestedUrl.includes('/auth/login/notes')) {
              return of(true);
            }
            return this.redirect('/auth/login/notes', requestedUrl);
          } else {
            return this.redirect('/auth/login/notes', requestedUrl);
          }
        }),
        catchError((error) => {
          if(error.status === 304 || error.status === 204) {
            this.gdprFine = true;
            return of(true);
          } else {
            return of(false);
          }
        })
      );
    }
  }

  private redirect(url: string, requestedUrl?: string): Observable<boolean> {
    this.routerService.setPostLoginRedirect(requestedUrl);
    this.routerService.navigateTo(url);
    return of(false);
  }

}
